import type { CSSProperties } from 'react'
import { useLocale } from '@/i18n/locale'

const COPY = {
  ru: {
    aria: 'Пример схемы, данные условные. Схема слоя систематизации: данные из источников компании попадают в сырьё, четыре наблюдателя разбирают их по функциям, узел сборки сверяет и записывает результат в три хранилища — компания, работа и навыки.',
    sample: 'Пример схемы · данные условные',
    sources: '.источники',
    sourceChips: ['учётная система', 'CRM', 'телефония', 'банки', 'почта и диск', 'таблицы', 'встречи'],
    raw: '.сырьё',
    rawChips: ['выгрузки', 'записи', 'документы'],
    observer: 'наблюдатель',
    observers: ['.люди', '.продукт', '.операции', '.рынок'],
    merge: 'сборка контекста',
    mergeChips: ['сверка', 'без дублей', 'запись'],
    stores: [
      ['.компания', 'устойчивые факты'],
      ['.работа', 'оперативная запись'],
      ['.навыки', 'библиотека компетенций'],
    ],
  },
  en: {
    aria: 'Sample diagram, illustrative data. Systematization layer diagram: data from company sources lands in raw data, four observers sort it by function, the merge node reconciles it and writes into three stores — company, work and skills.',
    sample: 'Sample diagram · illustrative data',
    sources: '.sources',
    sourceChips: ['accounting system', 'CRM', 'telephony', 'banks', 'mail and drive', 'spreadsheets', 'meetings'],
    raw: '.raw-data',
    rawChips: ['exports', 'recordings', 'documents'],
    observer: 'observer',
    observers: ['.people', '.product', '.operations', '.market'],
    merge: 'context merge',
    mergeChips: ['reconcile', 'dedupe', 'write'],
    stores: [
      ['.company', 'stable facts'],
      ['.work', 'operational record'],
      ['.skills', 'competency library'],
    ],
  },
}

// Центры колонок в процентах ширины. Сетки ниже намеренно без gap (зазор
// сделан внутренними полями ячеек): только так центр ячейки совпадает с
// этими числами на любой ширине, и линия приходит ровно в середину карточки.
const CENTER = [50]
const QUARTERS = [12.5, 37.5, 62.5, 87.5]
const THIRDS = [16.667, 50, 83.333]

/**
 * Связи между рядами. SVG растянут без сохранения пропорций, поэтому
 * бегущие точки — не <circle> (их сплющило бы в эллипс), а HTML-элементы:
 * внешний слой едет по X на долю СВОЕЙ ширины, внутренний — по Y на долю
 * своей высоты; оба равны полосе связи, так что точка идёт ровно по линии
 * при любой ширине макета и двигается одним transform, без пересчёта раскладки.
 */
function Links({ from, to, delay = 0 }: { from: number[]; to: number[]; delay?: number }) {
  const pairs = from.length === 1 ? to.map((x) => [from[0], x]) : from.map((x) => [x, to[0]])
  return (
    <div className="hiw-links -mx-1 sm:-mx-1.5" aria-hidden>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        {pairs.map(([a, b]) => (
          <line key={`${a}-${b}`} x1={a} y1={0} x2={b} y2={100} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      {pairs.map(([a, b], i) => (
        <span
          key={`${a}-${b}`}
          className="hiw-run-x"
          style={{ '--x0': `${a}%`, '--x1': `${b}%`, '--d': `${delay + i * 0.35}s` } as CSSProperties}
        >
          <span className="hiw-run-y">
            <span className="hiw-run-dot" />
          </span>
        </span>
      ))}
    </div>
  )
}

export function ContextFlowMock() {
  const t = COPY[useLocale()]
  return (
    <div
      role="img"
      aria-label={t.aria}
      className="hiw-mock rounded-card border border-divider bg-card font-mono text-ink p-4 sm:p-6"
    >
      {/* Та же метка, что на окне собственника (ТЗ 3A, правило честности):
          схема показывает устройство, а не результат конкретного клиента. */}
      <div className="mb-4 flex justify-end sm:mb-5">
        <span className="hiw-chip hiw-chip--sample">{t.sample}</span>
      </div>
      <div className="mx-auto max-w-[46rem]">
        <div className="hiw-node">
          <div className="hiw-node__name">{t.sources}</div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {t.sourceChips.map((c) => (
              <span key={c} className="hiw-chip hiw-chip--sm">{c}</span>
            ))}
          </div>
        </div>

        <Links from={CENTER} to={CENTER} />

        <div className="hiw-node mx-auto w-fit max-w-full">
          <div className="hiw-node__name">{t.raw}</div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {t.rawChips.map((c) => (
              <span key={c} className="hiw-chip hiw-chip--sm">{c}</span>
            ))}
          </div>
        </div>

        <Links from={CENTER} to={QUARTERS} delay={0.6} />

        <div className="-mx-1 grid grid-cols-4 sm:-mx-1.5">
          {t.observers.map((o) => (
            <div key={o} className="px-1 sm:px-1.5">
              <div className="hiw-node hiw-node--tight h-full">
                <div className="hidden text-[0.625rem] uppercase tracking-wider text-muted sm:block">{t.observer}</div>
                <div className="hiw-node__name hiw-node__name--fit sm:mt-1">{o}</div>
              </div>
            </div>
          ))}
        </div>

        <Links from={QUARTERS} to={CENTER} delay={1.2} />

        <div className="hiw-node hiw-node--accent mx-auto w-fit max-w-full">
          <div className="hiw-node__name">{t.merge}</div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {t.mergeChips.map((c) => (
              <span key={c} className="hiw-chip hiw-chip--sm hiw-chip--on-accent">{c}</span>
            ))}
          </div>
        </div>

        <Links from={CENTER} to={THIRDS} delay={1.8} />

        <div className="-mx-1 grid grid-cols-3 sm:-mx-1.5">
          {t.stores.map(([name, note]) => (
            <div key={name} className="px-1 sm:px-1.5">
              <div className="hiw-node hiw-node--tight h-full">
                <div className="hiw-node__name hiw-node__name--fit">{name}</div>
                <div className="mt-1.5 text-[0.6875rem] leading-snug text-muted sm:text-xs">{note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
